
import { Component, Prop, Mixins, Watch, Vue } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import {
  cloneDeep,
  isEmpty,
  mapValues,
  pickBy,
  some,
  values
} from 'lodash-es'
import { fireMixpanelEvents } from '~/plugins/mixpanel/mixpanel-filters'
import FilterList from '~/components/filter/FilterList'
import { setURLQuery, removeURLQuery } from '~/lib/url'
import DeadlineFilterBar from '~/components/filter/DeadlineFilterBar.vue'
import RangeFilterBar from '~/components/filter/RangeFilterBar.vue'
import RequirementFilterBar from '~/components/filter/RequirementFilterBar.vue'
import ScoreFilterBar from '~/components/filter/ScoreFilterBar.vue'
import PaymentFilterBar from '~/components/filter/PaymentFilterBar.vue'
import DashboardButton from '~/components/common/buttons/DashboardButton.vue'
import { applyFilters, selectedFilters } from '~/store/filters'
import CloseCrossCtrl from '~/components/common/ctrls/CloseCrossCtrl.vue'
import { Store } from '~/types/store'
import { manageWidgets } from '~/lib/utils/utils'

@Component({
  name: 'DashboardFiltersModal',
  components: {
    DeadlineFilterBar,
    RangeFilterBar,
    RequirementFilterBar,
    ScoreFilterBar,
    PaymentFilterBar,
    DashboardButton,
    CloseCrossCtrl
  },
  computed: {
    ...mapGetters({
      activeCardList: 'dashboard/activeCardList'
    })
  }
})
export default class DashboardFiltersModal extends Mixins(FilterList) {
  @Prop({ type: Function }) addFilter: any
  @Prop({ type: Function }) removeAllFilters: any

  appliedFilters = {}

  private manageWidgets = manageWidgets

  mounted() {
    this.manageWidgets(true)
  }

  beforeDestroy() {
    this.manageWidgets(false)
  }

  @Watch('$store.state.filters', { immediate: true, deep: true })
  syncLocalStoreStates(filters: { [key in Store.TabName]: Store.Filter.FilterSet }) {
    this.appliedFilters = Vue.observable(cloneDeep(filters[this.tabName]))
  }

  get selectedFilters() {
    return selectedFilters(this.appliedFilters as Store.Filter.FilterSet)
  }

  get filterParamsModifiedStates() {
    const getFilterModifiedStates = (filterValue, filterKey) => {
      const defaultFilterValues = this.rangeFilterInitValues(filterKey)
      const isParamModified = (paramValue, paramKey) => {
        const defaultValueKey = {
          min: 'possibleMin',
          max: 'possibleMax'
        }[paramKey] || paramKey
        const defaultValue = defaultFilterValues?.[defaultValueKey]
        return paramValue !== defaultValue
      }
      return mapValues(filterValue, isParamModified)
    }
    return mapValues(this.selectedFilters, getFilterModifiedStates)
  }

  get filtersModifiedStates() {
    const isAnyFilterParamModified = filterValue => some(values(filterValue))
    return mapValues(this.filterParamsModifiedStates, isAnyFilterParamModified)
  }

  get applyBtnText() {
    const length = applyFilters(this.selectedFilters, this.$accessor.tabList.tabList).length
    return `See ${length} ${this.$pluralize('result', length, 's')}`
  }

  private applyFilter(filterName: Store.Filter.Filter, criterion: Store.Filter.FilterRule) {
    this.appliedFilters[filterName] = criterion
  }

  private applyFilters() {
    const { $route: route, $router: router } = this
    const isFilterModified = (_filterValue, filterKey) => this.filtersModifiedStates[filterKey]
    const filters = pickBy(this.selectedFilters, isFilterModified)

    if (isEmpty(filters)) {
      this.clearFilters()
    } else {
      setURLQuery({ filters, route, router, withCleanUp: false })
      fireMixpanelEvents(this.$mixpanel, filters, this.tabName, this.$accessor)
    }
  }

  private clearFilters() {
    if (this.$accessor.filters.areFiltersApplied) {
      removeURLQuery(this.$route.params.tabName, this.$router)
      this.$accessor.filters.resetAllFilters({ tabName: this.tabName })
    } else {
      this.appliedFilters = Vue.observable(cloneDeep(this.$store.state.filters[this.tabName]))
    }
  }

  private close() {
    this.$emit('close')
  }
}
